// A model's BOM (the yard's "STD LISTS" workbook, one "<MODEL> BOXES" sheet per model): the parts every boat of
// the model gets (the standard sections) and the parts each option brings (an option section: "AXIOM 16"",
// "Bow Thruster", "IF MERCURY DOUBLE ENGINE"). A boat's BOM is the standard sections plus the option sections
// its order calls for, the rest deleted, as the warehouse does by hand in Excel today.
//
// Which order choices bring in an option section is a rule kept per model and section (BomLink): "when T-TOP
// is ticked", "when GARMIN is ticked and RADAR is ticked", "when the engines say V10". A section with no rule
// of its own gets one suggested from its heading (suggestBomLink), so most of a model works from the start; a
// person sets the rest on the BOM lists page, and can still tick a section in or out on one boat's order.
//
// Parts are counted by SAP part code (0009xxxxxx); a line the sheet lists without a code ("x") is counted by
// its description.
import { z } from 'zod';
import { MODEL_ORDER, modelKey } from './model.js';
import { ORDER_FILE_TEMPLATE, orderFileLabel, orderValueOn } from './orderFile.js';
// ── reading a sheet ─────────────────────────────────────────────────────────────────────────────
const text = (v) => (v == null ? '' : String(v).replace(/\s+/g, ' ').trim());
/** a SAP part code as ten digits ("9101937" → "0009101937"); '' when it isn't one */
export function partCode(v) {
    // (a number kept as text keeps Excel's ".0": "9107250.0")
    const s = text(v).replace(/\.0+$/, '').replace(/[\s.]/g, '');
    if (/^\d{10}$/.test(s))
        return s;
    if (/^9\d{6}$/.test(s))
        return `000${s}`;
    return '';
}
// a line with no code yet: the sheet writes an x (Latin or Greek)
const PLACEHOLDER = /^[xXχΧ]$/;
// the workbook's own check columns and formula leftovers
const JUNK = /^(#VALUE!|#REF!|#N\/A|Λάθος μήκος|Well Done!!!|✅ ?Αγγλικά|false|true|`|υπάρχει κενό)$/i;
const isJunk = (t) => !t || JUNK.test(t) || /^(Column|Στήλη)\s?\d+$/i.test(t) || /^UPD\b/i.test(t) || /^(QNT|NOTES|ITEM|BOXES|CODE|DEXION)$/i.test(t);
const isBoxText = (t) => /^(BOX|ΒΟΧ|BIG\b)/i.test(t);
// a box's own name ("BOX 30", "BIG 3"), not a heading that starts like one ("BIG SUNBED")
const isBoxName = (t) => /^(BOX|ΒΟΧ)\s*\d|^BIG\s*\d+$/i.test(t);
const OPT_REF = /^(OPT|ΟΠΤ)\w*$/i;
// a place in the warehouse: rack, row, shelf ("2DC1", "10RA2", "4HE1/2", "5ΙB1")
const POSITION = /^\d{1,2}[A-Za-zΑ-Ωα-ω]{2}\d(\/\d)?$/;
const HEAD = {
    code: /^(ΚΩΔΙΚΟΣ SAP|SAP)$/i,
    pos: /^(ΘΕΣΗ ΑΘ|DEXION)$/i,
    desc: /^(ΠΕΡΙΓΡΑΦΗ ΕΙΔΟΥΣ|ITEM)$/i,
    qty: /^(ΠΟΣΟΤΗΤΑ\/ΜΜ|QNT)$/i,
    box: /^BOXES$/i,
};
/** the headings that only group the options below them */
const GROUP_TITLE = /^(LAYOUT|DECK LAYOUT|ENGINES|NAVIGATION & INSTRUMENTS|INSTRUMENTS|DECK & COCKPIT|ELECTRIC EQUIPMENT|ACCESSORIES)\s*:?$/i;
function headerCols(r, prev) {
    const code = r.findIndex((c) => HEAD.code.test(c));
    if (code < 0)
        return null;
    const others = r.some((c, i) => i !== code && (HEAD.desc.test(c) || HEAD.qty.test(c) || /^(Column|Στήλη)\s?\d+$/i.test(c) || /^(Α\/Α|A\/A|CODE)$/i.test(c)));
    if (!others)
        return null;
    const find = (re, fallback) => { const i = r.findIndex((c) => re.test(c)); return i >= 0 ? i : fallback; };
    return {
        code,
        pos: find(HEAD.pos, prev?.pos ?? code + 1),
        desc: find(HEAD.desc, prev?.desc ?? code + 2),
        qty: find(HEAD.qty, prev?.qty ?? code + 3),
        box: find(HEAD.box, prev?.box ?? -1),
    };
}
/** a folded heading, for keys and matching: case, accents and punctuation set aside */
export function foldTitle(s) {
    return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '');
}
/**
 * Read one model's sheet (its rows as SheetJS gives them, header: 1): the standard sections, then, from the
 * second table (a second header row, or the first option heading), the option sections, each with its parts.
 */
export function parseBomSheet(rows, firstRow = 1) {
    const warnings = [];
    // the sheet's own "UPD 8/10/26" line: when it was last changed
    let updated = '';
    // each heading with its parts, and whether it came after the sheet's second header row (its second table)
    const raw = [];
    let cols = null;
    let headers = 0;
    let cur = null;
    for (const [n, row] of rows.entries()) {
        // the row's number on the sheet (rows[] holds every row of the sheet's range, blank ones too)
        const at = firstRow + n;
        const r = Array.from(row ?? [], text);
        if (!r.some(Boolean))
            continue;
        const upd = r.find((t) => /^UPD\b/i.test(t));
        if (upd)
            updated = upd.replace(/^UPD\.?\s*/i, '');
        const hc = headerCols(r, cols);
        if (hc) {
            headers++;
            cols = hc;
            if (headers >= 2)
                cur = null;
            continue;
        }
        if (!cols)
            continue;
        const c = cols;
        const codeCell = r[c.code] ?? '';
        const code = partCode(codeCell);
        const posCell = c.pos >= 0 ? r[c.pos] ?? '' : '';
        const desc = r[c.desc] ?? '';
        const qtyCell = row?.[c.qty];
        const qtyNum = typeof qtyCell === 'number' ? qtyCell : /^\d+(?:[.,]\d+)?$/.test(text(qtyCell)) ? Number(text(qtyCell).replace(',', '.')) : null;
        const boxCell = c.box >= 0 && r[c.box] && !isJunk(r[c.box]) ? r[c.box] : '';
        const hasDesc = desc !== '' && !isJunk(desc);
        // a part: it has a code (or the x of one to come, or a mistyped one), or it describes something with a
        // quantity, a box or a place in the warehouse; a heading has none of these, and a row with an OPT reference
        // is an option's heading even with a box written on it
        const optRow = OPT_REF.test(codeCell) || OPT_REF.test(posCell);
        const isItem = !optRow && (Boolean(code) || PLACEHOLDER.test(codeCell) || (hasDesc && (qtyNum != null || Boolean(boxCell) || POSITION.test(posCell) || /^\d{6,}$/.test(codeCell))));
        const lo = c.qty + 1;
        const hi = c.box >= 0 ? c.box : r.length;
        const noteOf = () => r.slice(lo, hi).filter((t) => t && !isJunk(t) && !isBoxText(t)).join(' · ');
        if (isItem) {
            if (!cur) {
                cur = { title: '(untitled)', ref: '', note: '', box: '', second: headers >= 2, from: at, to: at, items: [] };
                raw.push(cur);
            }
            cur.to = at;
            const box = boxCell || (r.find((t, i) => i > c.qty && isBoxText(t)) ?? '');
            const descText = hasDesc ? desc : r.find((t, i) => i > c.code && t && !isJunk(t) && !isBoxText(t) && !/^\d+$/.test(t) && t !== posCell) ?? '';
            const item = {
                code,
                pos: !isJunk(posCell) && posCell !== descText && !isBoxText(posCell) ? posCell : '',
                desc: descText,
                qty: qtyNum,
                qtyText: qtyNum == null ? text(qtyCell) : '',
                box,
                note: noteOf(),
            };
            // a code written but not a valid one (an x to come, a digit too many): kept as written
            if (!code && codeCell && !isJunk(codeCell))
                item.codeText = codeCell;
            cur.items.push(item);
            continue;
        }
        // a heading: the description column's words, else the code column's, else what else the row says; its
        // OPT code wherever the sheet put it, else (a standard section) the code in the first column
        const optRef = [codeCell, posCell].find((t) => /^(OPT|ΟΠΤ)\w*$/i.test(t)) ?? '';
        const lead = r[0] ?? '';
        const stdRef = /^[A-Za-zΑ-Ωα-ω.,]{1,6}\d{0,2}[A-Za-z]{0,3}$/.test(lead) && !isJunk(lead) && !/^(x|χ)$/i.test(lead) ? lead.replace(/^[.,]+/, '') : '';
        // (the description column is the heading as written, even one that starts like a box: "BIG SUNBED")
        const pick = hasDesc && !/^(OPT|ΟΠΤ)\w*$/i.test(desc) ? desc : [optRef === codeCell ? '' : codeCell, ...r.filter((_, i) => i !== c.code && i !== c.desc && i !== 0)]
            .find((t) => t && !isJunk(t) && !isBoxName(t) && !/^\d+[A-ZΑ-Ω]?$/.test(t) && !/^(OPT|ΟΠΤ)/i.test(t) && !/^\d+(\.\d+)?$/.test(t));
        // (a row of formulas or a stray mark between two headings goes with the section above it; the sheet's
        // UPD line stays where it is)
        if (!pick) {
            if (cur && !upd)
                cur.to = at;
            continue;
        }
        const headBox = [posCell, boxCell].find(isBoxText) ?? '';
        cur = { title: pick, ref: optRef || stdRef, note: noteOf().split(' · ').filter((t) => t !== pick).join(' · '), box: headBox, second: headers >= 2, from: at, to: at, items: [] };
        raw.push(cur);
    }
    // the options start at the second table, or (a sheet with one table) at the first grouping heading with
    // nothing under it (LAYOUT, ENGINES, DECK & COCKPIT …); everything before is standard
    let split = raw.findIndex((s) => s.second);
    if (split < 0)
        split = raw.findIndex((s) => !s.items.length && GROUP_TITLE.test(s.title));
    if (split < 0)
        split = raw.length;
    // a grouping heading (ENGINES, DECK & COCKPIT …) groups the option sections under it; any other heading is a
    // section, kept even when nothing is under it yet, as the sheet has it
    const out = [];
    const seen = new Map();
    let group = '';
    let groupRows = null;
    raw.forEach((s, i) => {
        const part = i < split ? 'std' : 'opt';
        if (!s.items.length && GROUP_TITLE.test(s.title)) {
            if (part === 'opt') {
                group = s.title.replace(/\s*:\s*$/, '');
                groupRows = [s.from, s.to];
            }
            return;
        }
        const base = `${part}:${foldTitle(s.title) || 'section'}`;
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        const section = { key: n > 1 ? `${base}#${n}` : base, title: s.title, part, group: part === 'opt' ? group : '', ref: s.ref, rows: [s.from, s.to], items: s.items };
        if (s.note)
            section.note = s.note;
        if (s.box)
            section.box = s.box;
        if (part === 'opt' && groupRows)
            section.groupRows = groupRows;
        out.push(section);
    });
    if (!out.some((s) => s.part === 'opt'))
        warnings.push('No option sections were found');
    if (!out.some((s) => s.part === 'std'))
        warnings.push('No standard sections were found');
    return { sections: out, warnings, updated };
}
/** the model a "<MODEL> BOXES" sheet is for ("ALPHA50 BOXES" → "50"), or '' */
export function bomSheetModel(sheet) {
    if (!/BOXES\s*$/i.test(sheet))
        return '';
    const m = modelKey(sheet.replace(/BOXES\s*$/i, '').trim());
    return MODEL_ORDER.includes(m) ? m : '';
}
/** the order's whole layout / engines, for a rule that looks for words anywhere in them */
export const BOM_PSEUDO_FIELDS = {
    '@LAYOUT': { label: 'Layout (bow, stern, seats)', keys: ['E', 'F', 'G', 'H', 'I'] },
    '@ENGINES': { label: 'Engines (all)', keys: ['Q', 'R', 'S', 'T', 'U', 'V', 'W'] },
};
/** a rule field's name, as people read it */
export const bomFieldLabel = (field) => BOM_PSEUDO_FIELDS[field]?.label ?? orderFileLabel(field);
/** the fields a rule can look at: the pseudo fields, then the template's rows that take a value */
export const BOM_RULE_FIELDS = [
    ...Object.entries(BOM_PSEUDO_FIELDS).map(([field, f]) => ({ field, label: f.label })),
    ...ORDER_FILE_TEMPLATE.filter((r) => r.kind === 'text' || r.kind === 'option').map((r) => ({ field: r.key, label: orderFileLabel(r.key) })),
];
const fold = (s) => s.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
function fieldValue(values, field) {
    const pseudo = BOM_PSEUDO_FIELDS[field];
    return pseudo ? pseudo.keys.map((k) => values[k] ?? '').filter(Boolean).join(' ') : values[field] ?? '';
}
export function condHolds(c, values) {
    const v = fieldValue(values, c.field);
    switch (c.op) {
        case 'on': return orderValueOn(v);
        case 'off': return !orderValueOn(v);
        case 'has': return Boolean(c.text?.trim()) && fold(v).includes(fold(c.text.trim()));
        case 'lacks': return !(Boolean(c.text?.trim()) && fold(v).includes(fold(c.text.trim())));
    }
}
export function linkIncludes(link, values) {
    if (link.mode === 'always')
        return true;
    if (link.mode === 'never')
        return false;
    return link.conds.length > 0 && link.conds.every((c) => condHolds(c, values));
}
/** a rule in words: "T-TOP ticked and GARMIN not ticked" */
export function bomLinkText(link) {
    if (link.mode === 'always')
        return 'Always';
    if (link.mode === 'never' || !link.conds.length)
        return 'Only when ticked on the order';
    return link.conds.map((c) => {
        const f = bomFieldLabel(c.field);
        switch (c.op) {
            case 'on': return `${f} ticked`;
            case 'off': return `${f} not ticked`;
            case 'has': return `${f} says “${c.text ?? ''}”`;
            case 'lacks': return `${f} doesn't say “${c.text ?? ''}”`;
        }
    }).join(' and ');
}
// Greek capitals that look like Latin ones, as headings mix them ("Τ-ΤΟP", "ΙF (600 MERCURY)"): a heading is
// matched as written and with them read as Latin, so Greek words still match too
const LOOKALIKE = { Α: 'A', Β: 'B', Ε: 'E', Ζ: 'Z', Η: 'H', Ι: 'I', Κ: 'K', Μ: 'M', Ν: 'N', Ο: 'O', Ρ: 'P', Τ: 'T', Υ: 'Y', Χ: 'X' };
const latin = (s) => s.replace(/[ΑΒΕΖΗΙΚΜΝΟΡΤΥΧ]/g, (c) => LOOKALIKE[c] ?? c);
const matchable = (s) => { const f = fold(s).replace(/\s+/g, ' ').trim(); return `${f} | ${latin(f)}`; };
// option rows by what their headings say (first match per field; a heading can name several)
const FIELD_RULES = [
    { field: 'BL', re: /T-?TOP SIDE TENT/ },
    { field: 'BM', re: /AFT SIDE TENT/ },
    { field: 'BA', re: /T-?TOP|HARD ?TOP/, unless: /SIDE TENT/ },
    { field: 'BB', re: /SHOX/ },
    { field: 'BD', re: /BACK ?-?TO ?-?BACK/ },
    { field: 'BE', re: /CABIN FRIDGE/ },
    { field: 'BG', re: /2ND FRIDGE/ },
    { field: 'BF', re: /WET ?BAR/ },
    { field: 'BH', re: /ELECTRIC(AL)? (PERMANENT )?TABLE|ΗΛΕΚΤΡΙΚΟ (ΜΟΝΙΜΟ )?ΤΡΑΠΕΖΙ/ },
    { field: 'BI', re: /\bTABLE\b|ΤΡΑΠΕΖΙ/, unless: /ELECTRIC|ΗΛΕΚΤΡΙΚ/ },
    { field: 'BN', re: /SUN ?TENT CLASSIC|ΠΤΥΣΣΟΜΕΝ/ },
    { field: 'BJ', re: /SUN ?TENT.*(AFT|ΠΡΥΜΝ)/, unless: /CLASSIC/ },
    { field: 'BK', re: /SUN ?TENT.*(BOW|ΠΛΩΡ)/ },
    { field: 'BO', re: /TOWING EYES|ΚΡΙΚΟΙ/ },
    { field: 'BP', re: /SKI ?POLE/ },
    { field: 'BQ', re: /FLAG HOLDER|ROLL ?BAR/ },
    { field: 'BR', re: /CABIN IN AFT/ },
    { field: 'BS', re: /CABIN IN BOW/ },
    { field: 'BT', re: /CARBON SHOWER/ },
    { field: 'BV', re: /UNDERWATER|U\/W|ΥΠΟΒΡΥΧ/ },
    { field: 'BW', re: /SHORE ?POWER|ΦΟΡΤΙΣΤ/ },
    { field: 'BX', re: /BOW ?THRUSTER/ },
    { field: 'BY', re: /SOLAR/ },
    { field: 'BZ', re: /LED BAR/ },
    { field: 'CA', re: /PORTABLE FRIDGE|ΦΟΡΗΤΟ ΨΥΓ/ },
    { field: 'CB', re: /TOILET|ΤΟΥΑΛΕΤ/ },
    { field: 'CC', re: /AFT HATCH/ },
    { field: 'CL', re: /AFT WINDLASS|ΠΙΣΩ ΕΡΓΑΤ/ },
    { field: 'CD', re: /WINDLASS|ΕΡΓΑΤΗ/, unless: /AFT WINDLASS|ΠΙΣΩ ΕΡΓΑΤ/ },
    { field: 'CE', re: /TRIM ?TABS/ },
    { field: 'CF', re: /WASH ?DOWN/, unless: /\bSTD\b/ },
    { field: 'CG', re: /COFF?EE? ?MAKER|ΚΑΦΕΤΙΕΡ/ },
    { field: 'CH', re: /\bA\/C\b|AIR ?CONDITION/ },
    { field: 'CI', re: /INVE[NR]TER/ },
    { field: 'CJ', re: /GENERATOR|ΓΕΝΝΗΤΡΙ/ },
    { field: 'CK', re: /WATER HEATER|ΘΕΡΜΟΣΙΦ/ },
    { field: 'CM', re: /AFT ANCHOR|\bFOB\b/ },
    { field: 'AF', re: /AUTOPILOT/ },
    { field: 'AE', re: /JOYSTI/ },
    { field: 'AD', re: /ELECTRIC STEERING/ },
    { field: 'AC', re: /HYDRA[YU]?L?I?C STEERING/ },
    { field: 'AS', re: /GMDSS/ },
    { field: 'AT', re: /\bAIS\b|\bAIS-/ },
    { field: 'AU', re: /FUSION.*(EXTRA|6)/ },
    { field: 'AV', re: /FUSION.*(BASIC|4)/ },
    { field: 'AW', re: /THERMAL/ },
    { field: 'AX', re: /RADAR/ },
    { field: 'AY', re: /SEARCH ?LIGHT/ },
    { field: 'AR', re: /\bVHF\b/ },
];
const NEGATED = /\b(WITHOUT|NO|NOT|EXCEPT)\b|ΧΩΡΙΣ/;
const SCREEN = /AXIOM|GARMIN \d|\d{1,2} ?(''|"|”)|SCREEN|\bX \d{2}"/;
const ENGINE_WORDS = /ENGINE|ENGIE|MERCURY|YAMAHA|VOLVO|YANMAR|SINGLE|DOUBLE|TRIPLE|ΤΡΙΜΗΧΑΝ|\d ?X ?\d{3}\b|\bV ?10\b|\bV8\b/;
/**
 * A rule suggested from a section's heading: the order rows it names (T-TOP, BOW THRUSTER, GARMIN with a
 * 12" screen, a double Mercury), "not" for WITHOUT / NO headings, and "GARMIN not ticked" for a heading
 * whose GARMIN twin is also on the sheet ("Radar" beside "Radar GARMIN"). A standard section is always in;
 * an option heading nothing matches is left to tick by hand.
 */
export function suggestBomLink(section, siblingTitles = []) {
    if (section.part === 'std')
        return { mode: 'always', conds: [] };
    const t = matchable(section.title);
    // WITHOUT / NO / NOT turns what follows it into "not ticked"
    const negAt = latin(fold(section.title)).search(NEGATED);
    const conds = [];
    const add = (c) => { if (!conds.some((x) => x.field === c.field))
        conds.push(c); };
    for (const rule of FIELD_RULES) {
        if (!rule.re.test(t) || rule.unless?.test(t))
            continue;
        const at = latin(fold(section.title)).search(rule.re);
        add({ field: rule.field, op: negAt >= 0 && at > negAt ? 'off' : 'on' });
    }
    // the instruments' make and the screens' size and count
    if (/RAYMARINE|AXIOM/.test(t))
        add({ field: 'AI', op: 'on' });
    if (/GARMIN/.test(t))
        add({ field: 'AJ', op: 'on' });
    if (SCREEN.test(t) && !/ENGINE/.test(t)) {
        if (/\b16\b/.test(t))
            add({ field: 'AM', op: 'on' });
        if (/\b12\b/.test(t))
            add({ field: 'AN', op: 'on' });
        if (/(^|\D)9 ?(''|"|”)/.test(t))
            add({ field: 'AO', op: 'on' });
        if (/SECOND|\b2 ?X\b/.test(t))
            add({ field: 'AP', op: 'on' });
        if (/THIRD|\b3 ?X\b/.test(t))
            add({ field: 'AQ', op: 'on' });
    }
    if (!/GARMIN|RAYMARINE|AXIOM/.test(t)) {
        const plain = (s) => latin(fold(s)).replace(/GARMIN/g, ' ').replace(/[^A-Z0-9]+/g, ' ').trim();
        const mine = plain(section.title);
        const twin = siblingTitles.some((o) => /GARMIN/.test(latin(fold(o))) && plain(o) === mine);
        if (twin)
            add({ field: 'AJ', op: 'off' });
    }
    // engines: the make, how many, and V10 or not
    if (ENGINE_WORDS.test(t) && !SCREEN.test(t)) {
        if (/MERCURY/.test(t) && !/EXCEPT MERCURY/.test(t))
            add({ field: 'T', op: 'on' });
        if (/YAMAHA/.test(t))
            add({ field: 'U', op: 'on' });
        if (/VOLVO/.test(t))
            add({ field: 'V', op: 'on' });
        if (/YANMAR/.test(t))
            add({ field: 'W', op: 'on' });
        const count = /SINGLE|\b1 ENGINE/.test(t) ? '1' : /DOUBLE|\b2 ?X\b|\b2 ENGINES|\b2X\d/.test(t) ? '2' : /TRIPLE|ΤΡΙΜΗΧΑΝ|\b3 ?X\b|\b3X\d/.test(t) ? '3' : /\b4 ?X\b|\b4X\d/.test(t) ? '4' : '';
        if (count)
            add({ field: 'S', op: 'has', text: count });
        if (/\bV ?10\b/.test(t))
            add({ field: '@ENGINES', op: /NOT V ?10/.test(t) ? 'lacks' : 'has', text: 'V10' });
        else if (/\b\d ?X ?600\b|\b600 MERCURY\b/.test(t))
            add({ field: '@ENGINES', op: 'has', text: '600' });
    }
    else if (/\b1 ENGINE\b|\b2 ENGINES\b/.test(t)) {
        add({ field: 'S', op: 'has', text: /\b1 ENGINE\b/.test(t) ? '1' : '2' });
    }
    // the layout's words, and a hull colour
    const layout = t.match(/\b(BIG SUNBED|SMALL SUNBED|U-?SHAPE|L-?SHAPE|STRAIGHT)\b/);
    if (layout)
        add({ field: '@LAYOUT', op: 'has', text: layout[1].replace(/^([UL])-?SHAPE$/, '$1-SHAPE') });
    if (/WHITE BOAT|BLACK BOAT/.test(t))
        add({ field: 'L', op: 'has', text: /WHITE/.test(t) ? 'WHITE' : 'BLACK' });
    // voltage: 110V is the American set-up, 230V the European one; a heading with a "USA 110V" twin is the
    // European one
    if (/\b110 ?V\b|\bUSA\b/.test(t))
        add({ field: 'AK', op: 'on' });
    else if (/\b2[23]0 ?V\b/.test(t) && conds.length)
        add({ field: 'AK', op: 'off' });
    else if (conds.length) {
        const bare = (s) => latin(fold(s)).replace(/\b110 ?V\b|\bUSA\b|\bONLY\b/g, ' ').replace(/[^A-Z0-9]+/g, ' ').trim();
        const mine = bare(section.title);
        if (siblingTitles.some((o) => /\b110 ?V\b|\bUSA\b/.test(latin(fold(o))) && bare(o) === mine))
            add({ field: 'AK', op: 'off' });
    }
    return conds.length ? { mode: 'when', conds } : { mode: 'never', conds: [] };
}
/**
 * Every section of the model's list for one boat: whether its rule (saved, else suggested) brings it in for
 * this order, and whether the boat's own ticks say otherwise.
 */
export function resolveBom(list, values, links = {}, picks = {}) {
    const titles = list.sections.filter((s) => s.part === 'opt').map((s) => s.title);
    return list.sections.map((section) => {
        const saved = links[section.key];
        const link = saved ?? suggestBomLink(section, titles);
        const auto = linkIncludes(link, values);
        const picked = picks[section.key];
        return { section, link, linkFrom: saved ? 'saved' : 'suggested', auto, picked, included: picked ?? auto };
    });
}
/** the parts of the sections a boat has, in the sheet's order */
export function bomLines(resolved) {
    return resolved.filter((r) => r.included).flatMap((r) => r.section.items.map((i) => ({ ...i, section: r.section.title, part: r.section.part })));
}
/** the box a part goes in, for sorting a picking list ("BOX 3" before "BOX12"; no box last) */
export function boxOrder(box) {
    const m = box.match(/(\d+)/);
    if (!box.trim())
        return [Number.MAX_SAFE_INTEGER, ''];
    return [m ? Number(m[1]) : Number.MAX_SAFE_INTEGER - 1, box.toUpperCase()];
}
/** parts summed by SAP code (by description when there is none) over one or more boats' lines */
export function partTotals(boats) {
    const out = new Map();
    boats.forEach((lines, boat) => {
        for (const l of lines) {
            const key = l.code || `desc:${fold(l.desc)}`;
            let t = out.get(key);
            if (!t) {
                t = { code: l.code, desc: l.desc, pos: l.pos, qty: 0, odd: 0, boats: 0, lines: 0, seen: -1 };
                out.set(key, t);
            }
            if (l.qty != null)
                t.qty += l.qty;
            else
                t.odd++;
            t.lines++;
            if (!t.pos && l.pos)
                t.pos = l.pos;
            if (t.seen !== boat) {
                t.boats++;
                t.seen = boat;
            }
        }
    });
    return [...out.values()]
        // (sums of fractions carry binary noise: 0.1 + 0.2 is 0.30000000000000004)
        .map(({ seen: _seen, ...t }) => ({ ...t, qty: Math.round(t.qty * 1000) / 1000 }))
        .sort((a, b) => (a.code && b.code ? a.code.localeCompare(b.code) : a.code ? -1 : b.code ? 1 : a.desc.localeCompare(b.desc)));
}
/** an SAP import file: part code and quantity per line, ';'-separated (as Greek Excel opens it), UTF-8 */
export function sapImportCsv(totals) {
    const cell = (s) => (/[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const rows = totals.filter((t) => t.code && t.qty > 0).map((t) => [t.code, String(t.qty), cell(t.desc)].join(';'));
    return ['Material;Quantity;Description', ...rows].join('\r\n');
}
// ── standard equipment ─────────────────────────────────────────────────────────────────────────
/**
 * The yard's standard-equipment list ("<MODEL> STD List 2025-2026" blocks down a sheet's columns): each
 * model's standard items, read as the order rows they tick ("2 X AXIOM 12" → RAYMARINE, 12'' SCREEN, SECOND
 * SCREEN; "SUN TENT AFT + AFT SIDE TENTS" → both).
 */
export function parseStdEquipment(rows) {
    const byCol = new Map();
    const found = new Map();
    rows.forEach((row) => {
        Array.from(row ?? [], text).forEach((t, col) => {
            if (!t)
                return;
            const head = t.match(/^(.+?)\s+STD\s+List\b/i);
            if (head) {
                const m = modelKey(head[1].trim());
                if (MODEL_ORDER.includes(m)) {
                    byCol.set(col, m);
                    if (!found.has(m))
                        found.set(m, { items: [], fields: new Set() });
                }
                else
                    byCol.delete(col);
                return;
            }
            const model = byCol.get(col);
            if (!model || GROUP_TITLE.test(t))
                return;
            const entry = found.get(model);
            entry.items.push(t);
            for (const f of stdFields(t))
                entry.fields.add(f);
        });
    });
    return [...found.entries()].map(([model, e]) => ({ model, items: e.items, fields: [...e.fields] }));
}
/** the order rows a standard item ticks */
export function stdFields(item) {
    const parts = item.split(/\s\+\s/);
    const out = new Set();
    for (const p of parts) {
        const t = matchable(p);
        const link = suggestBomLink({ title: p, part: 'opt', group: '' });
        for (const c of link.conds)
            if (c.op === 'on' && !c.field.startsWith('@') && c.field !== 'S')
                out.add(c.field);
        if (/\b4 SPEAKERS\b/.test(t))
            out.add('AV');
        if (/AXIOM/.test(t) && !/\b(16|12|9)\b/.test(t))
            out.add('AI');
    }
    return [...out];
}
// ── API shapes ─────────────────────────────────────────────────────────────────────────────────
const ItemSchema = z.object({
    code: z.string().max(20),
    pos: z.string().max(60),
    desc: z.string().max(400),
    qty: z.number().finite().nullable(),
    qtyText: z.string().max(60),
    box: z.string().max(80),
    note: z.string().max(400),
    codeText: z.string().max(40).optional(),
});
const SheetRow = z.number().int().min(1).max(1_048_576);
const SheetRows = z.tuple([SheetRow, SheetRow]);
const SectionSchema = z.object({
    key: z.string().min(1).max(240),
    title: z.string().min(1).max(300),
    part: z.enum(['std', 'opt']),
    group: z.string().max(200),
    ref: z.string().max(60),
    note: z.string().max(400).optional(),
    box: z.string().max(80).optional(),
    rows: SheetRows.optional(),
    groupRows: SheetRows.optional(),
    items: z.array(ItemSchema).max(600),
});
export const SEASON_ID = /^\d{2}-\d{2}$/;
export const BomUploadSchema = z.object({
    season: z.string().regex(SEASON_ID, 'A season like 26-27'),
    source: z.string().max(200),
    lists: z.array(z.object({
        model: z.enum(MODEL_ORDER),
        sheet: z.string().max(120),
        sections: z.array(SectionSchema).min(1).max(600),
    })).min(1).max(20),
});
export const BomCondSchema = z.object({
    field: z.string().min(1).max(12),
    op: z.enum(['on', 'off', 'has', 'lacks']),
    text: z.string().max(80).optional(),
});
export const BomLinkSchema = z.object({
    mode: z.enum(['when', 'always', 'never']),
    conds: z.array(BomCondSchema).max(12),
});
export const BomLinkSetSchema = z.object({
    model: z.enum(MODEL_ORDER),
    sectionKey: z.string().min(1).max(240),
    /** null puts the section back on its suggested rule */
    link: BomLinkSchema.nullable(),
});
export const BomStdSetSchema = z.object({
    items: z.array(z.object({
        model: z.enum(MODEL_ORDER),
        fields: z.array(z.string().min(1).max(4)).max(140),
    })).min(1).max(20),
});
/** the key of an order file for a boat with no SAP yet: its name, as the Order Sheet keys such a row */
export const unlistedOrderKey = (name) => `name:${name.trim().toUpperCase().replace(/\s+/g, ' ')}`.slice(0, 80);
/** a boat's SAP from its order-file key ('' for one keyed by name) */
export const orderKeySap = (key) => (key.startsWith('name:') ? '' : key);
export const OrderFileSaveSchema = z.object({
    sap: z.string().trim().min(1).max(80),
    season: z.string().regex(SEASON_ID, 'A season like 26-27'),
    model: z.string().trim().min(1).max(20),
    name: z.string().trim().max(200),
    /** the order's cells by Order Sheet column ('' clears one) */
    cells: z.record(z.string().max(2000)).refine((c) => Object.keys(c).length <= 200, 'Too many cells'),
    picks: z.record(z.boolean()).refine((p) => Object.keys(p).length <= 600, 'Too many sections'),
    /** a boat that isn't on the season's Overview (a stock boat, one not in Smartsheet yet): its Order Sheet row
     *  keeps its name and model too, as an imported one does */
    unlisted: z.boolean().optional(),
});
export const OrderFileKeySchema = z.object({
    sap: z.string().trim().min(1).max(80),
    key: z.string().trim().min(1).max(400),
});
//# sourceMappingURL=bom.js.map